import { useMutation } from '@tanstack/react-query';

import type {
  CancelServiceUsageParams,
  CancelServiceUsageResponse,
  CreateServicePriceParams,
  CreateServicePriceResponse,
  DeleteServiceResponse,
  FreezeServiceParams,
  MutationResponse,
  UpdateExpirationDateParams,
  UseServiceParams,
} from '@/types/api/mutations';

import { HttpClient } from '@/network/HttpClient';

export const useUseServiceMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; data?: UseServiceParams }) =>
      HttpClient.request({
        method: 'POST',
        url: params.url,
        data: params.data,
      }),
  });

export const useRevertToBookingMutation = () =>
  useMutation({
    mutationFn: (url: string) =>
      HttpClient.request({
        method: 'PUT',
        url,
      }),
  });

export const useFreezeServiceMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; data: FreezeServiceParams }) =>
      HttpClient.request({
        method: 'PUT',
        url: params.url,
        data: params.data,
      }),
  });

export const useUnfreezeServiceMutation = () =>
  useMutation({
    mutationFn: ({
      url,
      data,
    }: {
      url: string;
      data: { freeze_period_id: number };
    }) =>
      HttpClient.request<MutationResponse>({
        method: 'PUT',
        url,
        data,
      }),
  });

export const useUpdateExpirationDateMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; data: UpdateExpirationDateParams }) =>
      HttpClient.request({
        method: 'PUT',
        url: params.url,
        data: params.data,
      }),
  });

export const useDeleteServiceMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; id: number }) =>
      HttpClient.request<DeleteServiceResponse>({
        method: 'DELETE',
        url: `${params.url}/${params.id}`,
      }),
  });

export const useServiceFormMutation = () =>
  useMutation({
    mutationFn: (params: {
      url: string;
      method: 'POST' | 'PUT';
      data: FormData;
    }) =>
      HttpClient.request<MutationResponse>({
        method: params.method,
        url: params.url,
        data: params.data,
      }),
  });

export const useCancelServiceUsageMutation = () =>
  useMutation({
    mutationFn: (params: CancelServiceUsageParams) =>
      HttpClient.request<CancelServiceUsageResponse>({
        method: 'DELETE',
        url: params.url,
      }),
  });

export const useCreateServicePriceMutation = () =>
  useMutation({
    mutationFn: (params: { url: string; data: CreateServicePriceParams }) =>
      HttpClient.request<CreateServicePriceResponse>({
        method: 'POST',
        url: params.url,
        data: params.data,
      }),
  });
